import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { updateProduct, getDocumentById } from "../../firebase/firebaseCrud";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  CustomInput,
  CustomTextArea,
} from "../../components/Form/CustomInputField";
import CustomButton from "../../components/Form/CustomButton";
import FileUpload from "../../components/Form/FileUpload";
import MultipleSelector from "../../components/Form/MultipleSelector";
import { DataContext } from "../../context/data-context";
import convertToSlug from "../../utils/slugconverter";
import { useCart } from "../../context/cart-context";

const UpdateProduct = () => {
  let navigate = useNavigate();
  const { formData, setFormData, filledState, setFilledState } =
    React.useContext(DataContext);
  const [errorMsg, setErrorMsg] = React.useState("");

  const [contentPic, setContentPic] = React.useState("");

  const handleContentPicChange = (event) => {
    const selectedFile = event.target.files[0];
    setErrorMsg("");

    if (selectedFile) {
      setFormData({ ...formData, imgSrc: selectedFile });
      setContentPic(selectedFile.name);
    }
  };

  const category = ["Short", "Medium", "Long"];

  const handleNameSlug = () => {
    // console.log(name);
    let name = formData.name === "" ? product.name : formData.name;
    const newSlug = convertToSlug(name);
    setFormData({ ...formData, slug: newSlug });
  };

  const params = useParams();
  const { isLoading, setIsLoading } = useCart();

  const [product, setProduct] = React.useState("");

  const getProduct = async () => {
    const prods = await getDocumentById("product-list", params.id);
    setProduct(prods);
  };

  React.useEffect(() => {
    getProduct();
  }, [params.id]);

  // console.log("product", product);

  React.useEffect(() => {
    if (product === 0) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [product]);

  React.useEffect(() => {
    handleNameSlug();
  }, [formData.name]);

  const handleUpdate = async (e) => {
    e.preventDefault();

    // Retain existing values for fields not provided in the form
    const updatedData = {
      name: formData.name || product.name,
      price: formData.price || product.price,
      limit: formData.limit || product.limit,
      imgSrc: formData.imgSrc || product.imgSrc,
      slug: formData.slug || product.slug,
      categories: formData.categories || product.categories,
      color: formData.color || product.color,
      description: formData.description || product.description,
    };

    try {
      const newDoc = await updateProduct(
        "product-list",
        params.id,
        updatedData
      );

      // setErrorMsg(`Product-ID: ${newDoc}`);
      setFormData({
        ...formData,
        name: "",
        price: 0,
        limit: 0,
        imgSrc: "",
        slug: "",
        categories: "",
        color: "",
        description: "",
      });
      toast(`Product Updated with ID: ${params.id}`, {
        type: "success",
      });
      setTimeout(() => {
        navigate("/dashboard/admin/products");
      }, 3000);
      // Do something with the newDoc
    } catch (error) {
      console.error("Error creating document: ", error);
    }
    // }
  };

  return (
    <React.Fragment>
      <ToastContainer />

      <Box sx={{ maxWidth: 720 }}>
        {/* Current image preview */}
        {(formData.imgSrc || product.imgSrc) && (
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
              p: 3,
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Box
              component="img"
              src={formData.imgSrc || product.imgSrc}
              alt={product.name}
              sx={{
                width: 80,
                height: 80,
                objectFit: "cover",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                flexShrink: 0,
              }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>
                {product.name || "—"}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#6b7280", mt: 0.5 }}>
                Editing product · ID: {params.id}
              </Typography>
            </Box>
          </Box>
        )}

        {errorMsg && (
          <Alert
            severity="error"
            onClose={() => setErrorMsg("")}
            sx={{ mb: 3, borderRadius: "8px" }}
          >
            {errorMsg}
          </Alert>
        )}

        {/* Basic info card */}
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: "12px",
            border: "1px solid #e5e7eb",
            p: 3,
            mb: 3,
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111827", mb: 0.5 }}>
            Product Details
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#6b7280", mb: 3 }}>
            Update the product name, image URL, and description.
          </Typography>

          <Grid container spacing={2}>
            <Grid xs={12} sm={8} item>
              <CustomInput
                label="Product Name"
                type="text"
                className="w-full"
                onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setErrorMsg(""); }}
                value={formData.name === "" ? product.name : formData.name}
              />
            </Grid>
            <Grid xs={12} sm={4} item>
              <CustomInput
                label="Image URL"
                type="text"
                className="w-full"
                onChange={(e) => { setFormData({ ...formData, imgSrc: e.target.value }); setErrorMsg(""); }}
                value={formData.imgSrc === "" ? product.imgSrc : formData.imgSrc}
              />
            </Grid>
            <Grid xs={12} item>
              <CustomTextArea
                label="Product Description"
                type="text"
                rowNo={3}
                className="w-full"
                onChange={(e) => { setFormData({ ...formData, description: e.target.value }); setErrorMsg(""); }}
                value={formData.description === "" ? product.description : formData.description}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Pricing & inventory card */}
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: "12px",
            border: "1px solid #e5e7eb",
            p: 3,
            mb: 3,
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111827", mb: 0.5 }}>
            Pricing &amp; Inventory
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#6b7280", mb: 3 }}>
            Adjust price, stock quantity, and available colours.
          </Typography>

          <Grid container spacing={2}>
            <Grid xs={12} sm={4} item>
              <CustomInput
                label="Price (£)"
                type="number"
                className="w-full"
                onChange={(e) => { setFormData({ ...formData, price: e.target.value }); setErrorMsg(""); }}
                value={formData.price === 0 ? product.price : formData.price}
              />
            </Grid>
            <Grid xs={12} sm={4} item>
              <CustomInput
                label="Stock Quantity"
                type="number"
                className="w-full"
                onChange={(e) => { setFormData({ ...formData, limit: e.target.value }); setErrorMsg(""); }}
                value={formData.limit === 0 ? product.limit : formData.limit}
              />
            </Grid>
            <Grid xs={12} sm={4} item>
              <CustomInput
                label="Available Colours"
                type="text"
                className="w-full"
                onChange={(e) => { setFormData({ ...formData, color: e.target.value }); setErrorMsg(""); }}
                value={formData.color === "" ? product.color : formData.color}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Category card */}
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: "12px",
            border: "1px solid #e5e7eb",
            p: 3,
            mb: 3,
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111827", mb: 0.5 }}>
            Category
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#6b7280", mb: 3 }}>
            Update product categories.
          </Typography>
          <MultipleSelector label="Product Categories" data={category} />
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box
            onClick={() => navigate("/dashboard/admin/products")}
            sx={{
              fontSize: 13,
              color: "#6b7280",
              cursor: "pointer",
              textDecoration: "underline",
              "&:hover": { color: "#111827" },
            }}
          >
            ← Back to All Products
          </Box>
          <CustomButton
            type="submit"
            sx={{ height: "44px", px: 5 }}
            color="primary"
            className="text-sm !uppercase"
            onClick={handleUpdate}
          >
            Save Changes
          </CustomButton>
        </Box>
      </Box>
    </React.Fragment>
  );
};

export default UpdateProduct;
